//@ts-nocheck
import { useEffect, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon } from "lucide-react";
import { ChevronDownIcon } from "../icons";
import { BracketIcon, DisabledIcon, RequiredIcon } from "../icons/Icons";
import {
  fetchJobApplication,
  updateJobApplication,
} from "../../features/jobApplication/JobApplicationSlice";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { toast } from "sonner";
import JobApplicationQuestionSet from "./JobApplicationQuestionSet";
import { useJobApplicationContext } from "./JobApplicationContext";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const nameData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const emailData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const phoneData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const locationData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const resumeData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const coverData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const educationData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const experienceData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const ProfileSummaryData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const JobApplication = () => {
  const [activeSection, setActiveSection] =
    useState<string>("basic-information");
  const [name, setName] = useState(nameData[0]);
  const [email, setEmail] = useState(emailData[0]);
  const [phoneNumber, setPhoneNumber] = useState(phoneData[0]);
  const [location, setLocation] = useState(locationData[0]);
  const [resume, setResume] = useState(resumeData[0]);
  const [coverLetter, setCoverLetter] = useState(coverData[0]);
  const [educationDetails, setEducationDetails] = useState(educationData[0]);
  const [workExperience, setWorkExperience] = useState(experienceData[0]);
  const { jobId } = useParams<{ jobId: string }>();
  const dispatch = useAppDispatch();
  const { baseId } = useParams<{ baseId: string }>();
  const jobApplicationData = useAppSelector(
    (state) => state.jobApplication.fetchTeamsByIdData,
  );
  const { isAddingStep, setIsAddingStep, isReorderSelection } =
    useJobApplicationContext();
  const { isFormAsideOpen, isFormAsideView, selectedElement } = useAppSelector(
    (state: RootState) => state.questionSteps,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [profileSummary, setProfileSummary] = useState(ProfileSummaryData[0]);

  // Fetch job application details on mount
  useEffect(() => {
    if (jobId && baseId) {
      setIsLoading(true);
      dispatch(
        fetchJobApplication({ baseId: parseInt(baseId), id: jobId }),
      ).finally(() => {
        setIsLoading(false);
      });
    }
  }, [dispatch, jobId, baseId]);

  useEffect(() => {
    if (jobApplicationData?.data?.candidate_name) {
      setName(jobApplicationData.data.candidate_name);
    }
  }, [jobApplicationData]);

  // email
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_email) {
      setEmail(jobApplicationData.data.candidate_email);
    }
  }, [jobApplicationData]);

  // phonenumber
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_phone_status) {
      setPhoneNumber(jobApplicationData.data.candidate_phone_status);
    }
  }, [jobApplicationData]);

  //location
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_location_status) {
      setLocation(jobApplicationData.data.candidate_location_status);
    }
  }, [jobApplicationData]);

  // resume
  useEffect(() => {
    if (jobApplicationData?.data?.resume_upload_status) {
      setResume(jobApplicationData.data.resume_upload_status);
    }
  }, [jobApplicationData]);

  //cover
  useEffect(() => {
    if (jobApplicationData?.data?.cover_letter_status) {
      setCoverLetter(jobApplicationData.data.cover_letter_status);
    }
  }, [jobApplicationData]);

  //education
  useEffect(() => {
    if (jobApplicationData?.data?.education_details_status) {
      setEducationDetails(jobApplicationData.data.education_details_status);
    }
  }, [jobApplicationData]);

  // experience
  useEffect(() => {
    if (jobApplicationData?.data?.work_experience_status) {
      setWorkExperience(jobApplicationData.data.work_experience_status);
    }
  }, [jobApplicationData]);

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    e.preventDefault();

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
      setActiveSection(sectionId);
    }
  };

  // name
  const handleNameChange = (selectedValue) => {
    try {
      setName(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_name:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Name status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update name status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // email

  const handleEmailChange = (selectedValue) => {
    try {
      setEmail(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_email:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Email status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update email status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // phonenumber

  const handlePhoneChange = (selectedValue) => {
    try {
      setPhoneNumber(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_phone_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Phone Number status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update phone number status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // location
  const handleLocationChange = (selectedValue) => {
    try {
      setLocation(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_location_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Location status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update location status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // resume
  const handleProfileSummaryChange = (selectedValue) => {
    try {
      setProfileSummary(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            profile_summary_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Profile summary updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update Profile summary");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // resume
  const handleResumeChange = (selectedValue) => {
    try {
      setResume(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            resume_upload_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Resume status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update resume status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // cover
  const handleCoverChange = (selectedValue) => {
    try {
      setCoverLetter(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            cover_letter_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Cover status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update cover status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // education
  const handleEducationChange = (selectedValue) => {
    try {
      setEducationDetails(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            education_details_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Education status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update education status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // education
  const handleExperienceChange = (selectedValue) => {
    try {
      setWorkExperience(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            work_experience_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Experience status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update experience status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh_-_5rem)]">
        <div className="simple-spinner-loader">
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-screen">
        <div className=" py-[34px] lg:py-[70px] px-0 lg:px-0 h-full flex">
          <div className="px-[0px] sm:px-0 lg:px-0 w-full flex justify-center gap-x-[3rem]">
            <ul
              role="list"
              className={
                isAddingStep ||
                isReorderSelection ||
                isFormAsideOpen ||
                isFormAsideView
                  ? "space-y-1 flex flex-col gap-y-3 fixed mq1800:hidden mq3000:flex mq1800:left-[20%] mq3000:left-[20%]"
                  : "space-y-1 flex flex-col gap-y-3 fixed mq1800:left-[19%] mq3000:left-[29%]"
              }
            >
              <li className=" sidebar-nav">
                <a
                  onClick={(e) => handleNavClick(e, "basic-information")}
                  href="#basic-information"
                  className={classNames(
                    "  hover:bg-gray-10 capitalize group  items-center flex gap-x-[10px] p-0 text-[0.8125rem] m-0 w-full cursor-pointer leading-5 rounded-[4px] font-medium  focus:outline-none pr-[2px] pl-[6px]",
                    activeSection === "basic-information"
                      ? "text-black-100"
                      : "text-[#0000007a]",
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      activeSection === "basic-information"
                        ? "#000"
                        : "#0000007a"
                    }
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={classNames(
                      "icon icon-tabler icons-tabler-outline icon-tabler-lock  w-[1rem] h-[1rem]",
                      activeSection === "basic-information"
                        ? "text-black-100"
                        : "text-[#0000007a]",
                    )}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-6z" />
                    <path d="M11 16a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" />
                    <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
                  </svg>
                  Basic Information
                </a>
              </li>
              <li className=" sidebar-nav">
                <a
                  onClick={(e) => handleNavClick(e, "candidate-profile")}
                  href="#candidate-profile"
                  className={classNames(
                    "hover:bg-gray-10 capitalize group items-center flex gap-x-[10px] p-0 text-[0.8125rem] m-0 w-full cursor-pointer leading-5 rounded-[4px] font-medium  focus:outline-none pr-[2px] pl-[6px]",
                    activeSection === "candidate-profile"
                      ? "text-black-100"
                      : "text-[#0000007a]",
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      activeSection === "candidate-profile"
                        ? "#000"
                        : "#0000007a"
                    }
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={classNames(
                      "icon icon-tabler icons-tabler-outline icon-tabler-password-user w-[1rem] h-[1rem]",
                      activeSection === "candidate-profile"
                        ? "text-black-100"
                        : "text-[#0000007a]",
                    )}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M12 17v4" />
                    <path d="M10 20l4 -2" />
                    <path d="M10 18l4 2" />
                    <path d="M5 17v4" />
                    <path d="M3 20l4 -2" />
                    <path d="M3 18l4 2" />
                    <path d="M19 17v4" />
                    <path d="M17 20l4 -2" />
                    <path d="M17 18l4 2" />
                    <path d="M9 6a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
                    <path d="M7 14a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2" />
                  </svg>
                  Candidate Profile
                </a>
              </li>
              <li className=" sidebar-nav">
                <a
                  onClick={(e) => handleNavClick(e, "application-detail")}
                  href="#application-detail"
                  className={classNames(
                    " hover:bg-gray-10 capitalize group flex gap-x-[10px] p-0 text-[0.8125rem] m-0 w-full cursor-pointer leading-5 items-center rounded-[4px] font-medium  focus:outline-none pr-[2px] pl-[6px]",
                    activeSection === "application-detail"
                      ? "text-black-100"
                      : "text-[#0000007a]",
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={
                      activeSection === "application-detail"
                        ? "#000"
                        : "#0000007a"
                    }
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={classNames(
                      "icon icon-tabler icons-tabler-outline icon-tabler-lock-password w-[1rem] h-[1rem]",
                      activeSection === "application-detail"
                        ? "text-[#0000007a]"
                        : " text-black-100",
                    )}
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M5 13a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2z" />
                    <path d="M8 11v-4a4 4 0 1 1 8 0v4" />
                    <path d="M15 16h.01" />
                    <path d="M12.01 16h.01" />
                    <path d="M9.02 16h.01" />
                  </svg>
                  Application Detail
                </a>
              </li>
            </ul>

            <div className="w-full max-w-[640px] flex gap-x-[2rem]">
              <div className="w-[100%]">
                <main className="flex-1 bg-white h-full">
                  <div className=" w-full flex justify-center">
                    <div className="w-full">
                      <div className="flex flex-col gap-y-1 w-full">
                        <h2 className="text-[20px] lg:text-[24px] font-medium leading-8 text-[#1b1b1b] tracking-[-0.01rem]">
                          Job Application form
                        </h2>
                        <p className="text-sm leading-5 text-[#5c5c5e] font-medium">
                          Customize the application form for this job. Select
                          the fields and questions you want candidates to see
                          and answer.
                        </p>
                      </div>
                      <div className="bg-[#e0e0e0] w-full h-[1px] my-6"></div>
                      <div className="mt-6 lg:mt-7 ">
                        <div
                          className="py-4 border-b border-light-gray-200 flex justify-between"
                          id="basic-information"
                        >
                          <div className="w-[100%] flex flex-col gap-y-2">
                            <p className="text-[15px] font-medium leading-5 text-[#1b1b1b]">
                              Basic Information
                            </p>
                            <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                              Basic details
                            </p>
                            {/* name */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Name
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  disabled
                                  value={name}
                                  onChange={handleNameChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full  rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 opacity-65 cursor-not-allowed">
                                      <span>
                                        {name === "required" ? (
                                          <RequiredIcon />
                                        ) : name === "optional" ? (
                                          <BracketIcon />
                                        ) : name === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{name.icon}</span> // Optional fallback icon
                                        )}
                                      </span>

                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] text-dark-black-200 capitalize">
                                        {name?.name || name || "Select a name"}{" "}
                                        {/* Render name safely */}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm">
                                      {nameData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>

                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* email */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Email
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={email}
                                  onChange={handleEmailChange}
                                  disabled
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full  rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 opacity-65 cursor-not-allowed">
                                      <span>
                                        {email === "required" ? (
                                          <RequiredIcon />
                                        ) : email === "optional" ? (
                                          <BracketIcon />
                                        ) : email === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{email.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span
                                        className="col-start-1 row-start-1 truncate pr-6 ml-[-10px]
                                       text-dark-black-200 capitalize"
                                      >
                                        {" "}
                                        {email?.name ||
                                          email ||
                                          "Select a name"}{" "}
                                        {/* Render name safely */}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {emailData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block capitalize truncate font-normal group-data-[name]:font-semibold">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* phone */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Phone
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={phoneNumber}
                                  onChange={handlePhoneChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {phoneNumber === "required" ? (
                                          <RequiredIcon />
                                        ) : phoneNumber === "optional" ? (
                                          <BracketIcon />
                                        ) : phoneNumber === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{phoneNumber.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {phoneNumber?.name ||
                                          phoneNumber ||
                                          "Select a name"}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {phoneData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                           text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                          {/* icon */}
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>

                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* location */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Location
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={location}
                                  onChange={handleLocationChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {location === "required" ? (
                                          <RequiredIcon />
                                        ) : location === "optional" ? (
                                          <BracketIcon />
                                        ) : location === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{location.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {location?.name ||
                                          location ||
                                          "Select a name"}{" "}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {locationData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 
                                          data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="py-4 border-b border-light-gray-200  flex justify-between flex-wrap lg:flex-nowrap gap-2"
                          id="candidate-profile"
                        >
                          <div className="lg:w-[100%] w-full flex flex-col gap-y-2">
                            <p className="text-[15px] font-medium leading-5 text-[#1b1b1b]">
                              Candidate Profile
                            </p>
                            <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                              This is candidate profile details
                            </p>

                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Profile Summary
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={profileSummary}
                                  onChange={handleProfileSummaryChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {profileSummary === "required" ? (
                                          <RequiredIcon />
                                        ) : profileSummary === "optional" ? (
                                          <BracketIcon />
                                        ) : profileSummary === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{profileSummary.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {profileSummary?.name ||
                                          profileSummary ||
                                          "Select a name"}{" "}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {ProfileSummaryData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>

                            {/* resume */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Resume
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={resume}
                                  onChange={handleResumeChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {resume === "required" ? (
                                          <RequiredIcon />
                                        ) : resume === "optional" ? (
                                          <BracketIcon />
                                        ) : resume === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{resume.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {resume?.name ||
                                          resume ||
                                          "Select a name"}{" "}
                                      </span>
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {resumeData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* cover */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Cover Letter
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={coverLetter}
                                  onChange={handleCoverChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {coverLetter === "required" ? (
                                          <RequiredIcon />
                                        ) : coverLetter === "optional" ? (
                                          <BracketIcon />
                                        ) : coverLetter === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{coverLetter.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {coverLetter?.name ||
                                          coverLetter ||
                                          "Select a name"}{" "}
                                      </span>{" "}
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {coverData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 
                                          data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* educationDetails */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Education Details
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={educationDetails}
                                  onChange={handleEducationChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {educationDetails === "required" ? (
                                          <RequiredIcon />
                                        ) : educationDetails === "optional" ? (
                                          <BracketIcon />
                                        ) : educationDetails === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{educationDetails.icon}</span> // Optional fallback icon
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {educationDetails?.name ||
                                          educationDetails ||
                                          "Select a name"}{" "}
                                      </span>{" "}
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>

                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {educationData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 
                                          data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                            {/* work */}
                            <div className="w-full flex items-center justify-between lg:justify-start py-1">
                              <div className="wg-label inline-flex shrink-0 items-center gap-1 text-[14px] text-[#2f2f31] wg-antialiased  leading-5 w-[74%] capitalize">
                                Work Experience
                              </div>

                              <div className="w-[26%] flex flex-col justify-end">
                                <Listbox
                                  value={workExperience}
                                  onChange={handleExperienceChange}
                                >
                                  <div className="relative mt-0">
                                    <ListboxButton className="flex items-center justify-between w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6">
                                      <span>
                                        {workExperience === "required" ? (
                                          <RequiredIcon />
                                        ) : workExperience === "optional" ? (
                                          <BracketIcon />
                                        ) : workExperience === "disabled" ? (
                                          <DisabledIcon />
                                        ) : (
                                          <span>{workExperience.icon}</span>
                                        )}
                                      </span>
                                      <span className="col-start-1 row-start-1 truncate pr-6 ml-[-10px] capitalize">
                                        {workExperience?.name ||
                                          workExperience ||
                                          "Select a name"}{" "}
                                      </span>{" "}
                                      <ChevronDownIcon
                                        aria-hidden="true"
                                        className="col-start-1 row-start-1 size-4 self-center justify-self-end text-gray-500 sm:size-4"
                                      />
                                    </ListboxButton>
                                    <ListboxOptions
                                      transition
                                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                                    >
                                      {experienceData.map((person) => (
                                        <ListboxOption
                                          key={person.id}
                                          value={person.name}
                                          className="group relative cursor-default select-none py-2 pl-3 pr-9
                                          text-gray-900 data-[focus]:bg-gray-200 data-[focus]:text-gray-900 
                                          data-[focus]:outline-none"
                                        >
                                          <div className="flex items-center gap-x-2">
                                            <span>{person.icon}</span>
                                            <span className="block truncate font-normal group-data-[name]:font-semibold capitalize">
                                              {person.name}
                                            </span>
                                          </div>
                                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-[&:not([data-name])]:hidden group-data-[focus]:text-white">
                                            <CheckIcon
                                              aria-hidden="true"
                                              className="size-4"
                                            />
                                          </span>
                                        </ListboxOption>
                                      ))}
                                    </ListboxOptions>
                                  </div>
                                </Listbox>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div
                          className="py-4 border-b border-light-gray-200  flex justify-between flex-wrap lg:flex-nowrap gap-2"
                          id="application-detail"
                        >
                          <div className="lg:w-[100%] w-full flex flex-col gap-y-2">
                            <p className="text-[15px] font-medium leading-5 text-[#1b1b1b]">
                              Application Detail
                            </p>
                            <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                              Job application details
                            </p>
                            <JobApplicationQuestionSet />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default JobApplication;
